/**
 * Bearbeitungsblatt für Stammdaten.
 *
 * Kunde, Spielplatz und Gerät unterscheiden sich nur in ihren Feldern, nicht
 * im Ablauf: Blatt auf, Werte ändern, speichern, Fehler anzeigen. Deshalb ein
 * Baustein mit einer Feldbeschreibung statt drei fast gleicher Formulare –
 * sonst weicht das dritte irgendwann vom ersten ab, und niemand merkt es.
 *
 * Geschickt wird nur, was sich geändert hat. Das ist bei PATCH kein
 * Schönheitsfehler, sondern der Unterschied zwischen "Feld nicht angefasst"
 * und "Feld geleert": schickte man immer alles, überschriebe ein Speichern
 * Angaben, die jemand anderes zwischenzeitlich ergänzt hat.
 */

import { useState, type ReactNode } from 'react'
import { Blatt } from './Blatt'
import { Fehlerhinweis } from './Hinweise'

export type Feld = {
  name: string
  beschriftung: string
  art?: 'text' | 'zahl' | 'email' | 'tel' | 'mehrzeilig' | 'auswahl'
  optionen?: { wert: string; text: string }[]
  hinweis?: string
  /** Nimmt die volle Breite ein statt einer Spalte. */
  breit?: boolean
  pflicht?: boolean
}

type Werte = Record<string, unknown>

export function Bearbeiten({
  titel,
  untertitel,
  felder,
  werte,
  schliessen,
  speichern,
  laeuft,
  fehler,
  fuss,
}: {
  titel: string
  untertitel?: ReactNode
  felder: Feld[]
  werte: Werte
  schliessen: () => void
  speichern: (aenderungen: Record<string, string | number | null>) => void
  laeuft: boolean
  fehler: unknown
  /** Zusätzliches unterhalb der Felder, etwa ein Ausmustern-Knopf. */
  fuss?: ReactNode
}) {
  // Der Anfangsstand als Text, damit sich der Vergleich später einfach hält.
  const anfang = Object.fromEntries(
    felder.map((f) => [f.name, werte[f.name] == null ? '' : String(werte[f.name])]),
  )
  const [stand, setzeStand] = useState<Record<string, string>>(anfang)

  const geaendert = felder.filter((f) => stand[f.name] !== anfang[f.name])

  function absenden() {
    const nutzlast: Record<string, string | number | null> = {}
    for (const feld of geaendert) {
      const roh = stand[feld.name].trim()
      if (roh === '') {
        // Leeren heißt löschen. Ein Pflichtfeld kann nicht geleert werden,
        // das fängt schon das Formular ab.
        nutzlast[feld.name] = null
      } else if (feld.art === 'zahl') {
        nutzlast[feld.name] = Number(roh)
      } else {
        nutzlast[feld.name] = roh
      }
    }
    speichern(nutzlast)
  }

  return (
    <Blatt
      titel={titel}
      untertitel={untertitel}
      schliessen={schliessen}
      breit
      fuss={
        <div className="flex gap-3">
          <button type="button" className="knopf-still flex-1" onClick={schliessen}>
            Abbrechen
          </button>
          <button
            type="submit"
            form="bearbeiten"
            className="knopf-haupt flex-1"
            disabled={laeuft || geaendert.length === 0}
          >
            {laeuft
              ? 'Speichert …'
              : geaendert.length === 0
                ? 'Nichts geändert'
                : `Speichern (${geaendert.length})`}
          </button>
        </div>
      }
    >
      <form
        id="bearbeiten"
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault()
          absenden()
        }}
      >
        <div className="grid gap-3 sm:grid-cols-2">
          {felder.map((feld) => {
            const kennung = `feld-${feld.name}`
            const wert = stand[feld.name]
            const setzen = (neu: string) =>
              setzeStand((v) => ({ ...v, [feld.name]: neu }))

            return (
              <div key={feld.name} className={feld.breit ? 'sm:col-span-2' : undefined}>
                <label htmlFor={kennung}>
                  {feld.beschriftung}
                  {stand[feld.name] !== anfang[feld.name] && (
                    <span className="ml-2 font-normal text-akzent-600">geändert</span>
                  )}
                </label>

                {feld.art === 'mehrzeilig' ? (
                  <textarea
                    id={kennung}
                    value={wert}
                    onChange={(e) => setzen(e.target.value)}
                    rows={3}
                  />
                ) : feld.art === 'auswahl' ? (
                  <select
                    id={kennung}
                    value={wert}
                    onChange={(e) => setzen(e.target.value)}
                    required={feld.pflicht}
                  >
                    {!feld.pflicht && <option value="">— keine Angabe —</option>}
                    {(feld.optionen ?? []).map((o) => (
                      <option key={o.wert} value={o.wert}>
                        {o.text}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    id={kennung}
                    type={
                      feld.art === 'zahl'
                        ? 'number'
                        : feld.art === 'email'
                          ? 'email'
                          : feld.art === 'tel'
                            ? 'tel'
                            : 'text'
                    }
                    inputMode={feld.art === 'zahl' ? 'numeric' : undefined}
                    value={wert}
                    onChange={(e) => setzen(e.target.value)}
                    required={feld.pflicht}
                    autoCapitalize={feld.art === 'email' ? 'none' : undefined}
                  />
                )}

                {feld.hinweis && (
                  <p className="mt-1 text-base text-slate-600">{feld.hinweis}</p>
                )}
              </div>
            )
          })}
        </div>

        <Fehlerhinweis fehler={fehler} />
      </form>

      {fuss && <div className="mt-6 border-t-2 border-slate-200 pt-4">{fuss}</div>}
    </Blatt>
  )
}
